import { Button } from '@tempo-io/sui-button';
import { AccountTree, Add, KeyboardArrowDown, KeyboardArrowUp } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { forwardRef } from 'react';

interface ISubItemButton {
  count: number;
  isExpanded: boolean;
  onClick: () => void;
  canHaveLabel?: boolean;
  readOnly: boolean;
  canAddSubItems?: boolean;
  canAddSubItemsTooltip?: string;
}

function ToggleIcon({ count, isExpanded }: { count: number; isExpanded: boolean }) {
  return (
    <>
      <AccountTree size={14} />
      <p className="ml-1 text-sm">{count > 99 ? '99+' : count}</p>
      {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
    </>
  );
}

const getTitle = (count: number, isExpanded: boolean) => {
  if (count > 0) {
    return isExpanded ? 'Hide Sub-Items' : 'Show Sub-Items';
  }
  return 'Add Sub-Item';
};

export function SubItemButton({
  count,
  isExpanded,
  onClick,
  canHaveLabel,
  readOnly,
  canAddSubItems = true,
  canAddSubItemsTooltip = '',
}: ISubItemButton) {
  const title = getTitle(count, isExpanded);
  const tooltipTitle = !canAddSubItems && count === 0 ? canAddSubItemsTooltip : title;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {count > 0 ? (
          <Button size="sm" variant="ghost" aria-label="Sub Items" onClick={onClick}>
            <ToggleIcon count={count} isExpanded={isExpanded} />
          </Button>
        ) : (
          <AddSubItemsButton
            canHaveLabel={canHaveLabel}
            onClick={onClick}
            isDisabled={readOnly || !canAddSubItems}
            label={title}
            data-testid="add-sub-item-button"
          />
        )}
      </TooltipTrigger>

      <TooltipContent sideOffset={5} align="end">
        {tooltipTitle}
      </TooltipContent>
    </Tooltip>
  );
}

const AddSubItemsButton = forwardRef<
  HTMLButtonElement,
  Pick<ISubItemButton, 'canHaveLabel' | 'onClick'> & { label: string; isDisabled: boolean }
>(({ canHaveLabel, onClick, isDisabled, label, ...props }, ref) =>
  canHaveLabel ? (
    <Button
      size="sm"
      aria-label={label}
      onClick={onClick}
      disabled={Boolean(isDisabled)}
      ref={ref}
      variant="ghost"
      {...props}
    >
      <Add />
      {label}
    </Button>
  ) : (
    <Button
      size="icon"
      variant="ghost"
      aria-label={label}
      onClick={onClick}
      disabled={Boolean(isDisabled)}
      ref={ref}
      {...props}
    >
      <Add />
    </Button>
  ),
);
